import { CircularProgress, Collapse, InputAdornment, Tooltip, Typography } from '@mui/material'

import {
  OBJECT_PROPERTIES_QUERY_KEY,
  ObjectProperty,
  useObjectProperties,
} from 'api/objectProperties/objectProperties.api'
import { OptJobUnitSetting } from 'api/optJobs/optJobs.api'
import { useSettingMutation } from 'api/settings/settings.api'
import { useSystem } from 'api/systems/systems.api'
import { Unit, useUnits } from 'api/units/units.api'
import { clone } from 'helpers/global.helper/global.helper'
import { queryClient } from 'helpers/queryClient'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Switch, TextField } from 'ui/atoms'
import Datetime from 'utils/datetime/datetime'
import sentry from 'utils/sentry/sentry'
import { systemFeatureFlagActive } from 'utils/systemUtils'
import { useAuth } from '../AuthContext/AuthContext'
import styles from './OptModelsAccumulatorLevels.module.less'

type LevelValues = {
  unit: Unit
  startLevel: string
  endLevel: string
  minLevel: number
  maxLevel: number
  dynamicEndLevelAvailable: boolean
  dynamicEndLevelActive: boolean
  dynamicEndLevelValue: number
  dynamicEndLevelActiveId: number
  dynamicEndLevelValueId: number
}

type UnitErrors = { startLevel: boolean; endLevel: boolean }
type UnitBoundaries = { startLevel: number; endLevel: number; minLevel: number; maxLevel: number }

function roundToTwo(number: number): number {
  return Math.round((number + Number.EPSILON) * 100) / 100
}

type OptModelsAccumulatorLevelsProps = {
  setIsValid: (isValid: boolean) => void
  setUnitSettings: (unitSettings: Record<string, OptJobUnitSetting>) => void
  optModelId: number
}

export function OptModelsAccumulatorLevels({
  setIsValid,
  setUnitSettings,
  optModelId,
}: OptModelsAccumulatorLevelsProps): ReactElement {
  const { t } = useTranslation()
  const { systemId } = useAuth()
  const { data: activeSystem } = useSystem({ id: systemId })
  const { data: units = [], status: unitFetchStatus } = useUnits({ optModel: optModelId, unit_type: `Storage` })

  const { mutate: updateSetting } = useSettingMutation()
  const [toggleState, setToggleState] = useState<'pending' | 'error' | ''>('')
  const [unitIdToUnitMap, setUnitIdToUnitMap] = useState<Record<number, Unit>>({})
  const [showAccInputs, setShowAccInputs] = useState(false)

  // Track previous validity to avoid unnecessary parent updates
  const prevIsValidRef = useRef<boolean | null>(null)

  // Hide accumulator level information if the 'hide_acc_end_level_info' feature flag is active for the current system.
  const hideEndAccLevels = activeSystem ? systemFeatureFlagActive(activeSystem, 'hide_acc_end_level_info') : false

  // Initialize showAccInputs based on units count
  useEffect(() => {
    setShowAccInputs(units.length < 2)
  }, [units.length])

  // Initialize unitIdToUnitMap when units are fetched
  useEffect(() => {
    if (unitFetchStatus !== 'success') {
      setUnitIdToUnitMap({})
      return
    }

    const initUnitMap: Record<number, Unit> = {}
    for (const unit of units) {
      initUnitMap[unit.id] = clone(unit)
    }
    setUnitIdToUnitMap(initUnitMap)
  }, [units, unitFetchStatus])

  // [2023-12-21]: https://app-eu.wrike.com/open.htm?id=1148309904
  const { data: objectPropertyFictiveAckEndLevel = [] } = useObjectProperties({
    optModel: activeSystem?.primary_opt_model?.id,
    name: 'endlevel_fictive_income',
    start_time: Datetime.getISONow(0, true),
    end_time: Datetime.getISONow(1, true),
  })
  const { data: objectPropertyFictiveAckEndLevelEnabled = [] } = useObjectProperties({
    optModel: activeSystem?.primary_opt_model?.id,
    name: 'endcost_active',
    start_time: Datetime.getISONow(0, true),
    end_time: Datetime.getISONow(1, true),
  })

  // Combine object properties once
  const objectProperties: ObjectProperty[] = useMemo(
    () => [...objectPropertyFictiveAckEndLevel, ...objectPropertyFictiveAckEndLevelEnabled],
    [objectPropertyFictiveAckEndLevel, objectPropertyFictiveAckEndLevelEnabled]
  )

  // Build a map from unitId to its LevelValues (derived from API data)
  const unitIdToAccLevels: Record<string, LevelValues> = useMemo(() => {
    const result: Record<string, LevelValues> = {}

    for (const unit of units) {
      if (!unit.latest_storage_value) continue

      const {
        value: startLevel,
        min_level: minLevel,
        max_level: maxLevel,
        end_level: endLevel,
      } = unit.latest_storage_value

      let dynamicEndLevelAvailable = false
      let dynamicEndLevelActive = false
      let dynamicEndLevelValue = 0
      let dynamicEndLevelActiveId = 0
      let dynamicEndLevelValueId = 0

      for (const op of objectProperties) {
        if (op.parent?.id !== unit.id) continue

        if (op.name === 'endcost_active') {
          dynamicEndLevelAvailable = true
          dynamicEndLevelActive = op.current_active_value === 1
          dynamicEndLevelActiveId = op.id
        }

        if (op.name === 'endlevel_fictive_income') {
          dynamicEndLevelValue = op.current_active_value ?? op.default_value
          dynamicEndLevelValueId = op.id
        }
      }

      result[unit.id] = {
        unit,
        startLevel: String(roundToTwo(startLevel)),
        endLevel:
          endLevel != null && endLevel > maxLevel
            ? String(roundToTwo(maxLevel))
            : endLevel != null
              ? String(roundToTwo(endLevel))
              : '',
        minLevel,
        maxLevel,
        dynamicEndLevelAvailable,
        dynamicEndLevelActive,
        dynamicEndLevelValue,
        dynamicEndLevelActiveId,
        dynamicEndLevelValueId,
      }
    }

    return result
  }, [units, objectProperties])

  // Compute errors and boundaries from local state (unitIdToUnitMap) merged with API level data
  const { unitIdToErrors, unitIdToBoundaries, isValid } = useMemo(() => {
    const unitIdToErrors: Record<string, UnitErrors> = {}
    const unitIdToBoundaries: Record<string, UnitBoundaries> = {}
    let hasErrors = false

    for (const [unitId, unit] of Object.entries(unitIdToUnitMap)) {
      const accLevel = unitIdToAccLevels[unitId]
      const { minLevel = 0, maxLevel = 0 } = accLevel ?? {}
      const startLevel = unit.latest_storage_value?.value ?? 0
      const endLevel = unit.latest_storage_value?.end_level ?? 0

      const errors: UnitErrors = {
        startLevel: minLevel > startLevel || startLevel > maxLevel,
        endLevel: minLevel > endLevel || endLevel > maxLevel,
      }

      if (errors.startLevel || errors.endLevel) {
        hasErrors = true
      }

      unitIdToErrors[unitId] = errors
      unitIdToBoundaries[unitId] = { minLevel, maxLevel, startLevel, endLevel }
    }

    return {
      unitIdToErrors,
      unitIdToBoundaries,
      isValid: !hasErrors,
    }
  }, [unitIdToAccLevels, unitIdToUnitMap])

  // Notify parent of validity changes via useEffect (not inside useMemo)
  // Only call setIsValid when the value actually changes to prevent infinite loops
  useEffect(() => {
    if (prevIsValidRef.current !== isValid) {
      prevIsValidRef.current = isValid
      setIsValid(isValid)
    }
  }, [isValid, setIsValid])

  // Notify parent of unit settings changes
  // Compute settings and call setUnitSettings only when unitIdToUnitMap changes
  useEffect(() => {
    const unitSettings: Record<string, OptJobUnitSetting> = {}

    for (const unit of Object.values(unitIdToUnitMap)) {
      unitSettings[unit.name] = {
        start_level: unit.latest_storage_value?.value,
        end_level: unit.latest_storage_value?.end_level,
      }
    }

    setUnitSettings(unitSettings)
  }, [unitIdToUnitMap, setUnitSettings])

  // Handler for updating a unit's storage value
  const handleUnitStorageChange = useCallback((unitId: string, field: 'value' | 'end_level', newValue: number) => {
    setUnitIdToUnitMap((prev) => {
      const unit = prev[Number(unitId)]
      if (!unit) return prev

      return {
        ...prev,
        [unitId]: {
          ...unit,
          latest_storage_value: {
            ...unit.latest_storage_value,
            [field]: newValue,
          },
        },
      }
    })
  }, [])

  async function handleDynamicEndLevelToggle(
    dynamicEndLevelActiveId: number,
    dynamicEndLevelAvailable: boolean,
    dynamicEndLevelActive: boolean
  ): Promise<void> {
    if (!dynamicEndLevelAvailable || toggleState === 'pending') {
      return
    }

    setToggleState('pending')

    try {
      await updateSetting({
        data: {
          object_property: dynamicEndLevelActiveId,
          value: dynamicEndLevelActive ? 0 : 1,
          start_time: Datetime.getISONow(),
        },
      })

      // NOTE: Invalidation is triggered before update updateSetting, even when await is used. Could be an internal debounce in react-query. Hence the timeout.
      await new Promise((resolve) => setTimeout(resolve, 500))
      await queryClient.invalidateQueries({ queryKey: [OBJECT_PROPERTIES_QUERY_KEY] })
      setToggleState('')
    } catch (error: unknown) {
      sentry.captureException(error as Error, {
        level: 'warning',
        tags: {
          sentry_scope: 'opt_model_accumulator_levels',
          issue_type: 'toggle_dynamic_end_level_failure',
        },
        extra: {
          dynamicEndLevelActiveId,
          dynamicEndLevelActive,
        },
        fingerprint: ['opt_model_accumulator_levels', 'toggle_dynamic_end_level_failure'],
      })
      setToggleState('error')
    }
  }

  const accumulatorInputs = Object.entries(unitIdToUnitMap).map(([unitId, unit]) => {
    const accLevel = unitIdToAccLevels[unitId]
    const errors = unitIdToErrors[unitId]
    const boundaries = unitIdToBoundaries[unitId]

    if (!errors || !boundaries) return null

    const { minLevel, maxLevel, startLevel, endLevel } = boundaries

    return (
      <div key={unitId}>
        {Object.keys(unitIdToUnitMap).length >= 2 && <Typography>{unit.display_name}</Typography>}
        <div className={styles.OptModelsAccumulatorLevels_InputRow}>
          <div className={styles.OptModelsAccumulatorLevels_InputItem}>
            <Tooltip
              title={
                activeSystem?.automatic_optimization
                  ? t(`Accumulator levels cannot be changed when automatic optimization is active`)
                  : ''
              }
              placement="top"
              arrow
            >
              <TextField
                type="number"
                value={roundToTwo(startLevel)}
                label={t('Start level accumulator')}
                inputProps={{ endAdornment: <InputAdornment position="end">MWh</InputAdornment> }}
                onChange={(value) => handleUnitStorageChange(unitId, 'value', Number(value))}
                error={errors.startLevel}
                disabled={activeSystem?.automatic_optimization}
                helperText={
                  errors.startLevel
                    ? t(`Range between {{lower}} - {{upper}}`, { lower: minLevel, upper: maxLevel })
                    : undefined
                }
              />
            </Tooltip>
          </div>
          {!hideEndAccLevels && (
            <div className={styles.OptModelsAccumulatorLevels_InputItem}>
              <Tooltip
                title={
                  activeSystem?.automatic_optimization
                    ? t(`Accumulator levels cannot be changed when automatic optimization is active`)
                    : ''
                }
                placement="top"
                arrow
              >
                <TextField
                  type="number"
                  value={roundToTwo(endLevel)}
                  label={
                    accLevel?.dynamicEndLevelAvailable && accLevel?.dynamicEndLevelActive
                      ? t('Max end level accumulator')
                      : t('End level accumulator')
                  }
                  inputProps={{ endAdornment: <InputAdornment position="end">MWh</InputAdornment> }}
                  error={errors.endLevel}
                  disabled={activeSystem?.automatic_optimization}
                  helperText={
                    errors.endLevel
                      ? t(`Range between {{lower}} - {{upper}}`, { lower: minLevel, upper: maxLevel })
                      : undefined
                  }
                  onChange={(value) => handleUnitStorageChange(unitId, 'end_level', Number(value))}
                />
              </Tooltip>
            </div>
          )}
          {!hideEndAccLevels && accLevel?.dynamicEndLevelAvailable && (
            <div className={styles.OptModelsAccumulatorLevels_SwitchItem}>
              <Switch
                value={
                  accLevel?.dynamicEndLevelAvailable && accLevel?.dynamicEndLevelActive ? t(`Dynamic`) : t(`Static`)
                }
                items={[
                  {
                    value: t(`Dynamic`),
                    label: t(`Dynamic`),
                    tooltip: t(`Setting for end level in accumulator`),
                  },
                  {
                    value: t(`Static`),
                    label: t(`Static`),
                    tooltip: t(`Setting for end level in accumulator`),
                  },
                ]}
                onClick={() => {
                  if (!accLevel) {
                    setToggleState('error')
                    return
                  }

                  handleDynamicEndLevelToggle(
                    accLevel.dynamicEndLevelActiveId,
                    accLevel.dynamicEndLevelAvailable,
                    accLevel.dynamicEndLevelActive
                  )
                }}
              />
              {toggleState && (
                <CircularProgress size={25} className={styles.OptModelsAccumulatorLevels_CircularProgress} />
              )}
            </div>
          )}
        </div>
      </div>
    )
  })

  if (units.length >= 2) {
    return (
      <div>
        <Button link={true} onClick={() => setShowAccInputs(!showAccInputs)}>
          {t('Start and end levels for accumulators')}
        </Button>
        <Collapse in={showAccInputs} className={styles.OptModelsAccumulatorLevels_Collapse}>
          <div className={styles.OptModelsAccumulatorLevels_AccumulatorInputs}>{accumulatorInputs}</div>
        </Collapse>
      </div>
    )
  }

  return <div>{accumulatorInputs}</div>
}
